import React, { useLayoutEffect, useState } from 'react';
import './ChargingStationPanel.scss';
import AhmIcon from 'components/Global/AhmIcon/AhmIcon';
import {
  IChargingStation,
  IListConnector,
  IStationConnector,
} from 'interfaces/chargingLocator';

interface IChargingStationPanel {
  stationData: IChargingStation;
  labels: {
    acuraEVApp: string;
    details: string;
    connectors: string;
    amenities: string;
    resultDisclaimer: string;
  };
  onClose: () => void;
}

const ChargingStationPanel = ({
  stationData,
  labels,
  onClose,
}: IChargingStationPanel): JSX.Element => {
  const [animate, setAnimate] = useState(false);
  const totalConnectors = stationData.attributes.connectors.reduce(
    (total, current) => total + (current.amount || 0),
    0
  );

  const stationDetails = [
    ...stationData.attributes.access,
    ...stationData.attributes.accessRestrictions,
    ...stationData.attributes.parking,
  ];

  const hideAndClose = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
    }, 800);
  };

  const finalConnectors = (connectors: IStationConnector[]) => {
    var finalResult: IListConnector[] = [];
    connectors.forEach((element) => {
      finalResult = [...finalResult, ...element.listConnectors];
    });
    return finalResult;
  };

  const isHonda = process.env.REACT_APP_THEME === 'honda';
  const typeVariation = isHonda
    ? 'charging-station-panel-honda'
    : 'charging-station-panel';

  useLayoutEffect(() => {
    setTimeout(() => {
      setAnimate(true);
    });
  }, []);

  return (
    <div
      className={`${typeVariation} custom-scroll-dark ${
        animate ? 'active' : ''
      }`}
    >
      <div className={`${typeVariation}__mobile-holder`}>
        <div className={`${typeVariation}__eyebrow`} />
      </div>
      <div className={`${typeVariation}__header`}>
        {isHonda ? (
          <p className="ahm-legacy-label3 charging-station-panel-honda__title">
            {stationData.name}
          </p>
        ) : (
          <h2 className="ahm-legacy-heading5 charging-station-panel__title">
            {stationData.name}
          </h2>
        )}
        <div className="charging-station-panel__network-container">
          <p className={`ahm-legacy-label3 ${typeVariation}__network-title`}>
            {stationData.network?.name !== undefined
              ? `${stationData.network?.name} Network`
              : 'Network Name Unavailable'}
          </p>
          {stationData.isEvApp && (
            <p className={`ahm-legacy-label4 ${typeVariation}__network-pill`}>
              {isHonda ? 'Pay with HondaLink' : labels.acuraEVApp}
            </p>
          )}
        </div>
        <button
          className="charging-station-panel__close"
          onClick={hideAndClose}
        >
          {isHonda ? (
            <AhmIcon iconName="close" color="#00324f" />
          ) : (
            <AhmIcon iconName="close" />
          )}
        </button>
      </div>
      <div className="charging-station-panel__body">
        {stationData.address && (
          <div className="charging-station-panel__section">
            {isHonda ? (
              <p
                className={`ahm-legacy-label3 ${typeVariation}__section-title`}
              >
                {labels.details}
              </p>
            ) : (
              <h3
                className={`ahm-legacy-label3 ${typeVariation}__section-title`}
              >
                {labels.details}
              </h3>
            )}
            <div className={`${typeVariation}__group-container`}>
              <div className="charging-station-panel__group-item">
                <AhmIcon
                  classes={`${typeVariation}__icon`}
                  iconName={'location'}
                />
                <p className={`ahm-legacy-label3 ${typeVariation}__item-text`}>
                  {stationData.address}
                </p>
              </div>
              {stationDetails.map((element) => (
                <div className="charging-station-panel__group-item">
                  <img
                    className={`${typeVariation}__icon`}
                    alt={`${element.name} icon`}
                    src={element.iconUrl}
                  />
                  <p
                    className={`ahm-legacy-label3 ${typeVariation}__item-text`}
                  >
                    {element.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        {totalConnectors > 0 && (
          <div className="charging-station-panel__section">
            {isHonda ? (
              <p
                className={`ahm-legacy-label3 ${typeVariation}__section-title`}
              >
                {`${labels.connectors} (${totalConnectors})`}
              </p>
            ) : (
              <h3
                className={`ahm-legacy-label3 ${typeVariation}__section-title`}
              >
                {`${labels.connectors} (${totalConnectors})`}
              </h3>
            )}
            <div className={`${typeVariation}__group-container`}>
              {finalConnectors(stationData.attributes.connectors).map(
                (element) => (
                  <div className="charging-station-panel__group-item">
                    <img
                      className={`${typeVariation}__icon`}
                      alt={`${element.id} connector icon`}
                      src={element.iconUrl || ''}
                    />
                    <div>
                      <p
                        className={`ahm-legacy-label3 ${typeVariation}__item-text`}
                      >
                        {`${element.speed} kW • ${element.quantifier}`}
                      </p>
                      <p
                        className={`ahm-legacy-label4 ${typeVariation}__white-text`}
                      >
                        {`${
                          element.amount
                        } ${labels.connectors?.toLocaleLowerCase()}`}
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
        {stationData.attributes.amenities.length > 0 && (
          <div className="charging-station-panel__section">
            {isHonda ? (
              <p
                className={`ahm-legacy-label3 ${typeVariation}__section-title`}
              >
                {labels.amenities}
              </p>
            ) : (
              <h3
                className={`ahm-legacy-label3 ${typeVariation}__section-title`}
              >
                {labels.amenities}
              </h3>
            )}
            <div className={`${typeVariation}__group-container`}>
              {stationData.attributes.amenities.map((element) => (
                <div className="charging-station-panel__group-item">
                  <img
                    className={`${typeVariation}__icon`}
                    alt={`${element.name} icon`}
                    src={element.iconUrl}
                  />
                  <p
                    className={`ahm-legacy-label3 ${typeVariation}__item-text`}
                  >
                    {element.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={`${typeVariation}__disclaimer-container`}>
        <p className={`ahm-legacy-label4 ${typeVariation}__disclaimer`}>
          {labels.resultDisclaimer}
        </p>
      </div>
    </div>
  );
};

export default ChargingStationPanel;
